import { css } from 'styled-components';

/**
 * The system's keyboard focus treatment, as a bare declaration so there is exactly one
 * definition of what focus looks like.
 *
 * Drawn as an `outline` rather than a `box-shadow` so it never competes with the
 * three-shadow vocabulary and never affects layout. Signal Teal reaches 10.8:1 against
 * every surface in the ink ramp.
 *
 * Applied globally to every interactive element in `App.tsx` (`:focus-visible`), and
 * re-used here by the two controls that hide their native input and must ring a visual
 * proxy instead (Switch, Checkbox).
 *
 * Text-entry controls deliberately opt out via their own `outline: none` plus a Signal
 * Teal border on `:focus-within` — showing focus on click is correct when the user is
 * about to type.
 */
export const focusRingDecl = css`
  outline: 2px solid ${({ theme }) => theme.colors.primary};
  outline-offset: 2px;
`;

/**
 * Cancels the global ring for a control that draws its own focus treatment.
 *
 * A bare `outline: none` is not enough. The global rule is
 * `:where(a, button, input, …):focus-visible`, and `:where()` zeroes only its own
 * arguments — the trailing `:focus-visible` still counts, so that rule lands at the same
 * specificity as a styled-component class and injection order decides the winner. When
 * the global wins, the control shows BOTH its own Signal Teal border and the offset ring:
 * two concentric teal lines, which reads as a rendering bug.
 *
 * One class plus one pseudo-class beats it outright, whatever the order.
 */
export const focusRingOptOutDecl = css`
  &:focus-visible {
    outline: none;
  }
`;
